import React, { useContext } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import get from 'lodash/get'
import BootstrapDataContext from '../context/BootstrapDataContext'
import DEFAULT_META_TAGS from '../constants/meta'
import { Partner } from '../types'

type Favicon = {
  href: string
  rel: string
  type: string
}

const renderFavicons = (favicons: Favicon[]) =>
  favicons.map(favicon => <link key={favicon.href} {...favicon} />)

const Meta = ({ partner }: { partner?: Partner }) => {
  const {
    isMobile = false,
    baseUrl,
    insideTrackTitle,
    referral,
    prePopData
  } = useContext(BootstrapDataContext)
  const router = useRouter()
  const { pathname = '/vacationpackages' } = router
  const { image, url, description, title } = DEFAULT_META_TAGS
  const metaUrl = get(partner, 'config.head.meta.websiteUrl', url)
  const currentUrl = partner ? baseUrl : url
  const cityName = prePopData?.hotels?.endLocation?.cityName
  const isSEMCampaign = referral?.clickId?.includes('hotel') && cityName
  const pricelineTitle = isSEMCampaign
    ? `The Best Deals on ${cityName} Hotels at Priceline.com`
    : title[pathname as keyof typeof title]
  const isHotelsLP = ['/hotels', '/hotels/'].includes(pathname)
  const isFlightLP = ['/flights', '/flights/'].includes(pathname)
  const isVIPLP = ['/vip', '/vip/'].includes(pathname)
  const isHomepageLP = ['/'].includes(pathname)

  let metaDescription = get(
    partner,
    'config.head.meta.description',
    description.vacationpackages
  )
  if (isFlightLP) {
    metaDescription = description.flights
  } else if (isHotelsLP) {
    metaDescription = description.hotels
  } else if (isVIPLP) {
    metaDescription = description.vip
  } else if (isHomepageLP) {
    metaDescription = description.homepage
  }

  const pageUrl = `${currentUrl}${pathname}`
  const metaImage = get(partner, 'config.head.meta.imageUrl', image)

  const twitterHandle = get(
    partner,
    'config.head.meta.twitterHandle',
    '@priceline'
  )

  const brandName = partner?.config?.head?.meta?.brandName
  const partnerTitle =
    partner?.config?.head?.meta?.landingPageTitle ||
    `${brandName} - Hotel & Flight Packages`
  const metaTitle = partner ? partnerTitle : insideTrackTitle || pricelineTitle

  const favicons: Favicon[] = get(partner, 'config.head.meta.favicons', [
    {
      href: '/favicon.ico',
      rel: 'shortcut icon',
      type: 'image/x-icon'
    }
  ])
  const metaConstants = partner?.config?.head?.meta
  const partnerGoogleVerification = metaConstants?.googleVerification

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:url" content={metaUrl} />
        <meta name="twitter:image" content={metaImage} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content={twitterHandle} />
        <meta name="robots" content="noydir,noodp" />
        <meta name="language" content="english" />
        <meta
          name="description"
          className="metaDescription"
          content={metaDescription}
        />
        <meta
          name="google-site-verification"
          content="NfW_2qut5D0YEqJE2_8tqKYUKrWju1NsVFUc4Kxl7Sw"
        />
        <meta
          name="google-site-verification"
          content="F8oXNWFYF1zWWLC_bBMEiUaKq588GpL8o0r0r30sQtk"
        />
        {partnerGoogleVerification && (
          <meta
            name="google-site-verification"
            content={partnerGoogleVerification}
          />
        )}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no height=device-height"
        />
        <meta httpEquiv="Pragma" content="no-cache" />
        <meta httpEquiv="Expires" content="-1" />
        <meta httpEquiv="Expires" content="Tue, 01 Jan 1980 1:00:00 GMT" />
        <meta
          httpEquiv="Cache-Control"
          content="no-store, no-cache, must-revalidate, post-check=0, pre-check=0"
        />
        <meta httpEquiv="Cache-Control" content="max-age=0" />
        {renderFavicons(favicons)}
        <link rel="canonical" href={pageUrl} />
        {!partner && (
          <link
            rel="alternate"
            href="android-app://com.priceline.android.negotiator/pclnnegotiator/home"
          />
        )}
        <link
          rel="preconnect"
          href="https://fonts.googleapis.com"
          crossOrigin="true"
        />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="true"
        />
        <title>{metaTitle}</title>
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css?family=Montserrat:${
            isMobile ? '400,600' : '500,700'
          }`}
        />
        <script
          async
          src="/common/metrics/boomerang.js"
          type="application/javascript"
        />
      </Head>
    </>
  )
}

export default Meta
